import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { Nullable } from '@silverhand/essentials';
import type { TFuncKey } from 'i18next';
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import BrandingHeader from '@/components/BrandingHeader';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import usePlatform from '@/hooks/use-platform';
import DynamicT from '@/shared/components/DynamicT';
import NavBar from '@/shared/components/NavBar';
import PageMeta from '@/shared/components/PageMeta';
import { onKeyDownHandler } from '@/shared/utils/a11y';

import { InlineNotification } from '../../components/Notification';

import styles from './index.module.scss';

type Props = {
  readonly title: TFuncKey;
  readonly description?: TFuncKey | ReactElement | '';
  readonly titleProps?: Record<string, unknown>;
  readonly descriptionProps?: Record<string, unknown>;
  readonly notification?: TFuncKey;
  readonly onSkip?: () => void;
  readonly isNavBarHidden?: boolean;
  readonly logo?: Nullable<string>;
  readonly children: React.ReactNode;
};

const SecondaryPageLayout = ({
  title,
  description,
  titleProps,
  descriptionProps,
  notification,
  onSkip,
  isNavBarHidden,
  logo,
  children,
}: Props) => {
  const { t } = useTranslation();
  const { isMobile } = usePlatform();
  const navigate = useNavigateWithPreservedSearchParams();

  const onBack = () => {
    navigate(-1);
  };

  const header = logo ? (
    <div className={styles.brandedHeader}>
      <div
        role="button"
        tabIndex={0}
        aria-label={t('action.nav_back')}
        className={styles.backButton}
        onKeyDown={onKeyDownHandler(onBack)}
        onClick={onBack}
      >
        <ArrowBackIcon />
      </div>
      <BrandingHeader logo={logo} logoWrapperClassName={styles.logoWrapper} />
    </div>
  ) : (
    <NavBar isHidden={isNavBarHidden} onSkip={onSkip} onBack={onBack} />
  );

  const descriptionNode =
    description && typeof description === 'string' ? (
      <DynamicT forKey={description} interpolation={descriptionProps} />
    ) : (
      description
    );

  const titleBlock = logo ? null : (
    <div className={styles.header}>
      <div className={styles.title}>
        <DynamicT forKey={title} interpolation={titleProps} />
      </div>
      {description && <div className={styles.description}>{descriptionNode}</div>}
    </div>
  );

  return (
    <div className={styles.wrapper}>
      <PageMeta titleKey={title} />
      {header}
      {isMobile && notification && (
        <InlineNotification message={notification} className={styles.notification} />
      )}
      <div className={styles.container}>
        {titleBlock}
        {children}
      </div>
      {!isMobile && notification && (
        <InlineNotification message={notification} className={styles.notification} />
      )}
    </div>
  );
};

export default SecondaryPageLayout;
